/* eslint-disable @typescript-eslint/no-magic-numbers */
import { unique } from "@evergis/api";
import { StrokeBrushType, ClassifyAble, ParameterValue } from "../../../interfaces/style";
import { getParameterValue } from "../../../evergis/core/classification/getFromClassified";
import { clamp } from "../../../utils/math";
import { PREVIEW_LIMITS } from "./previewLimits";

export const enum StrokeStyle {
  Solid,
  Dash,
  Dot,
  DashDot,
  DashDotDot,
  Custom,
}

interface StrokeStylePreset {
  style: StrokeStyle;
  type: StrokeBrushType["type"];
  intervals: number[];
}
export const SOLID_INTERVALS: number[] = [];

export const solidStrokeStylePreset: StrokeStylePreset = {
  style: StrokeStyle.Solid,
  type: "solid",
  intervals: SOLID_INTERVALS,
};
export const strokeStylePresets: StrokeStylePreset[] = [
  solidStrokeStylePreset,
  {
    style: StrokeStyle.Dot,
    type: "dashed",
    intervals: [2, 2],
  },
  {
    style: StrokeStyle.Dash,
    type: "dashed",
    intervals: [3, 3, 3, 3],
  },
  {
    style: StrokeStyle.DashDot,
    type: "dashed",
    intervals: [5, 2, 1, 2],
  },
  {
    style: StrokeStyle.DashDotDot,
    type: "dashed",
    intervals: [5, 2, 1, 1, 1, 2],
  },
  {
    style: StrokeStyle.Custom,
    type: "dashed",
    intervals: [5, 4],
  },
];

const walkByNumbersGroup = (
  numbers: number[],
  offset: number,
  walker: (group: [number, number], offset: number) => [number, number],
): number[] => {
  if (!numbers || !numbers.length) return [];

  const newNumbers = [];

  for (let i = 0; i < numbers.length; i++) {
    if (i % 2) {
      newNumbers.push(...walker([numbers[i - 1], numbers[i]], offset));
    }
  }

  return newNumbers;
};

export const toIntervals = (numbers: number[], width: ClassifyAble<number>): number[] =>
  walkByNumbersGroup(
    numbers,
    clamp(getParameterValue(width), PREVIEW_LIMITS.STROKE_WIDTH_MIN, PREVIEW_LIMITS.STROKE_WIDTH_MAX),
    ([length, gap], offset) => [length * offset, gap * offset],
  );

export const toLineDash = (numbers: number[], width: ClassifyAble<number>) =>
  walkByNumbersGroup(
    numbers,
    clamp(getParameterValue(width), PREVIEW_LIMITS.STROKE_WIDTH_MIN, PREVIEW_LIMITS.STROKE_WIDTH_MAX),
    ([length, gap], offset) => [
      Math.round(length / Math.max(1, Math.min(offset, 16) / 4)),
      Math.round(gap + 1 + Math.max(1, Math.min(offset, 16) / 2)),
    ],
  );

export const getLineDash = (brush: StrokeBrushType): number[] => {
  if (!brush || brush.type === "solid") return [];

  return toLineDash(getParameterValue(brush.intervals as ParameterValue) as number[], brush.width);
};

export const defineStrokeStylePreset = (intervals: number[]) => {
  if (!intervals.length) return solidStrokeStylePreset;

  return strokeStylePresets.find(preset => {
    if (preset.intervals.length !== intervals.length) return false;

    const uniqueStrokeWidths = preset.intervals
      .map((presetInterval, i) => intervals[i] / presetInterval)
      .filter(unique);

    if (uniqueStrokeWidths.length === 1) {
      return true;
    }
  });
};

export const getDashStylePreset = (dashStyle: StrokeStyle): StrokeStylePreset | null =>
  strokeStylePresets.find(preset => preset.style === dashStyle);
